import { useEffect, useRef, useState } from 'react';
import { REAL_MS_PER_CALL_MINUTE } from '../lib/callTiming.js';

const WARN_SECONDS = 600; // 10 (call-)minutes

/** Client-side session countdown, in call-time. `remainingSeconds` is the number
 *  of call-seconds left; it elapses in real time at REAL_MS_PER_CALL_MINUTE per
 *  call-minute (60s in prod, faster under the test knob). Starts when `active`
 *  first becomes true; `warning` latches at ≤10 call-minutes, `ended` at 0.
 *  `extend(n)` pushes the deadline out by n call-minutes. */
export function useSessionTimer({ durationMinutes, active }: { durationMinutes: number | null; active: boolean }) {
  const deadlineRef = useRef<number | null>(null);
  const [remainingSeconds, setRemaining] = useState<number>((durationMinutes ?? 0) * 60);
  const [ended, setEnded] = useState(false);

  // Real ms left → remaining call-seconds.
  const toCallSeconds = (realMsLeft: number) => Math.round((realMsLeft / REAL_MS_PER_CALL_MINUTE) * 60);

  useEffect(() => {
    if (!active || durationMinutes == null) return;
    if (deadlineRef.current == null) deadlineRef.current = Date.now() + durationMinutes * REAL_MS_PER_CALL_MINUTE;
    const tick = () => {
      const callSecs = toCallSeconds(Math.max(0, deadlineRef.current! - Date.now()));
      setRemaining(callSecs);
      if (callSecs <= 0) setEnded(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, durationMinutes]);

  function extend(minutes: number) {
    if (deadlineRef.current == null) return;
    deadlineRef.current += minutes * REAL_MS_PER_CALL_MINUTE;
    setEnded(false);
    setRemaining(toCallSeconds(Math.max(0, deadlineRef.current - Date.now())));
  }

  return { remainingSeconds, warning: remainingSeconds > 0 && remainingSeconds <= WARN_SECONDS, ended, extend };
}
